import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import {
  Button,
  Divider,
  Heading,
  Modal as TRModal,
  StackView,
  Text,
} from '@planningcenter/tapestry-react'
import { ThemeProvider } from '~/components/ThemeProvider'

function Modal({ application, cancel, confirm, open }) {
  const name = application.isPersisted ? <code>{application.name}</code> : null
  const clientIdMessage = application.isPersisted
    ? "we'll delete the one associated with this application"
    : "we won't generate one for this application"

  return (
    <TRModal
      id="modal"
      style={{ width: '640px', maxWidth: '640px' }}
      height="auto"
      open={open}
    >
      <StackView spacing={2}>
        <Heading color="grey-9" weight={600} level={2} size={1}>
          Are you sure you want to make this application public?
        </Heading>

        <Text size={3} style={{ lineHeight: '1.65' }}>
          You're about to make your OAuth application {name} public. Once you
          submit the form, this can't be undone.
        </Text>

        <Text size={3} style={{ lineHeight: '1.65' }}>
          Since public OAuth applications are unable to safeguard a{' '}
          <code>client_secret</code>, {clientIdMessage}.
        </Text>

        <Divider marginTop={2} />

        <StackView
          axis="horizontal"
          distribution="end"
          marginVertical={1}
          spacing={1}
        >
          <Button
            title="Cancel"
            theme="default"
            variant="naked"
            size="lg"
            onClick={cancel}
            fontWeight={500}
          />

          <Button
            title="Yes"
            theme="interaction"
            variant="fill"
            size="lg"
            onClick={confirm}
            fontWeight={500}
          />
        </StackView>
      </StackView>
    </TRModal>
  )
}

export default function ConfirmPublicApplication({ application, form }) {
  const [open, setOpen] = useState(false)
  const select = useRef()

  const confirm = useCallback(() => {
    select.current.value = 'false'
    setOpen(false)
  }, [select])

  const cancel = useCallback(() => {
    select.current.value = 'true'
    setOpen(false)
  }, [select])

  useEffect(() => {
    function initiateConfirmation(event) {
      if (event.target.value === 'false') {
        setOpen(true)
      }
    }

    select.current = document
      .getElementById(form.id)
      .querySelector(`#${form.fieldId}`)
    select.current.addEventListener('change', initiateConfirmation)

    return () => {
      setOpen(false)
      select.current.removeEventListener('change', initiateConfirmation)
    }
  }, [form])

  return (
    <React.Fragment>
      {createPortal(
        <ThemeProvider>
          <Modal
            application={application}
            cancel={cancel}
            confirm={confirm}
            open={open}
          />
        </ThemeProvider>,
        document.body
      )}
    </React.Fragment>
  )
}

ConfirmPublicApplication.propTypes = {
  application: PropTypes.shape({
    name: PropTypes.string,
    isPersisted: PropTypes.bool.isRequired,
  }).isRequired,
  form: PropTypes.shape({
    id: PropTypes.string.isRequired,
    fieldId: PropTypes.string.isRequired,
  }).isRequired,
}
